import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "../../../../redux/store";
import {
	ICard3Data,
	// IOption,
	IVDMDashboardContent,
} from "redux/app/vdmdashboard/@types";
import { Cell, Pie, PieChart } from "recharts";

interface CustomizedLabelProps {
	cx: number;
	cy: number;
	midAngle: number;
	outerRadius: number;
	name: string;
	value: number;
	fill: string;
}

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
	cx,
	cy,
	midAngle,
	outerRadius,
	name,
	value,
	fill,
	percent, // ← add this
}: CustomizedLabelProps & { percent: number }) => {
	if (value === 0) return null;

	const sin = Math.sin(-midAngle * RADIAN);
	const cos = Math.cos(-midAngle * RADIAN);

	const isSingleSlice = percent === 1; // ← 100% means only one slice

	const outerEdgeX = cx + outerRadius * cos;
	const outerEdgeY = cy + outerRadius * sin;

	const elbowRadius = outerRadius * 1.18;
	const elbowX = cx + elbowRadius * cos;
	const elbowY = cy + elbowRadius * sin;

	// Force right side if single slice, otherwise derive from position
	const isRightSide = isSingleSlice ? true : elbowX >= cx;

	const horizontalExtend = 18;
	const labelX = isSingleSlice
		? cx + outerRadius + horizontalExtend // ← fixed offset from pie edge
		: elbowX + (isRightSide ? horizontalExtend : -horizontalExtend);
	const labelY = isSingleSlice ? cy : elbowY; // ← vertically centered on single

	const anchor = isRightSide ? "start" : "end";

	// For single slice, draw a straight horizontal line from the right edge
	const connectorStart = isSingleSlice
		? { x: cx + outerRadius, y: cy }
		: { x: outerEdgeX, y: outerEdgeY };

	const elbowPoint = isSingleSlice
		? { x: labelX, y: cy }
		: { x: elbowX, y: elbowY };

	return (
		<g>
			<polyline
				points={`${connectorStart.x},${connectorStart.y} ${elbowPoint.x},${elbowPoint.y} ${labelX},${labelY}`}
				fill="none"
				stroke={fill}
				strokeWidth={1}
			/>
			<circle cx={elbowPoint.x} cy={elbowPoint.y} r={1.5} fill={fill} />

			<text
				x={labelX + 3}
				y={labelY - 5}
				textAnchor={anchor}
				fill="#374151"
				fontSize={9}
				fontWeight={500}
			>
				{name}
			</text>

			<text
				x={labelX + 3}
				y={labelY + 7}
				textAnchor={anchor}
				fill={fill}
				fontSize={9}
				fontWeight={500}
			>
				{`${value} hr`}
			</text>
		</g>
	);
};

type OwnProps = {
	// data: ICard3Data;
	data?: IVDMDashboardContent["operationalStatus"];
};
type Card3Props = PropsFromRedux & OwnProps;
function Card3(props: Card3Props) {
	const { data } = props;

	if (!data) return null;

	const {
		hoursAtAnchor,
		hoursAtPort,
		hoursAtSea,
		hoursAtStoppage,
		// totalLengthOfDay,
	} = data;
	const chartData = [
		{ name: "Stoppages", value: hoursAtStoppage, color: "#ff8a65" },
		{ name: "At Sea", value: hoursAtSea, color: "#81d4fa" },
		{ name: "At Port", value: hoursAtPort, color: "#81c784" },
		{ name: "At Anchor", value: hoursAtAnchor, color: "#ffd54f" },
	];

	return (
		<div className="flex-1 hover:cursor-pointer 2xl:w-full">
			<div className="bg-gradient-to-b from-white to-[#F9FCFE] border border-gray-200 rounded-xl py-4 pl-4 pr-0 shadow-sm lg:max-w-[360px] xl:max-w-auto h-[179px] min-w-[176px] 2xl:max-w-full flex flex-col justify-between hover:shadow relative">
				<div className="flex">
					<div className="flex flex-col justify-between">
						<div>
							<h3 className="text-xs text-gray-800 font-medium mb-1 py-1 border-l-4 border-l-[#3182CE] px-2 bg-gradient-to-r from-[#3182CE29] to-[#3182CE00] absolute">
								Operational Status
							</h3>
							<p className="text-sm lg:text-base xl:text-lg font-medium text-gray-800 mb-3 mt-[28px]">
								Manned
								{/* TODO: This should be from api */}
							</p>
						</div>
						{/* INFO: Not part of phase-1 */}
						{/* <div className="space-y-2 text-xs mb-1">
							{metricsToShow?.map((metric: IOption, index: number) => {
								const { label, value } = metric;
								return (
									<div key={label + value} className="flex gap-2 items-center">
										<span className="text-gray-500 font-medium inline-block w-[70px]">
											{label}
										</span>
										<span className="text-gray-700 font-medium">{value}</span>
									</div>
								);
							})}
						</div> */}
					</div>
					<div className="w-full h-full">
						<PieChart
							margin={{ top: -15, right: 25, left: 10 }}
							width={230}
							height={173}
						>
							<Pie
								data={chartData}
								cx="50%"
								cy="55%"
								innerRadius={15}
								outerRadius={55}
								fill="#8884d8"
								paddingAngle={0}
								dataKey="value"
								labelLine={false}
								label={renderCustomizedLabel}
							>
								{chartData.map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={entry.color}
										stroke={entry.color}
									/>
								))}
							</Pie>
						</PieChart>
					</div>
				</div>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Card3);
